import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/App';
import { getMyCourses, getLessons } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BookOpen, Users, FileText, ChevronDown, ChevronUp } from 'lucide-react';

export default function InstructorCourses() {
  const navigate = useNavigate();
  const { currentInstructor } = useAppContext();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [lessonsMap, setLessonsMap] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMyCourses();
      setCourses(res.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  // The data loader is memoized; CRA's lint stack still flags this pattern here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentInstructor?.id) loadData();
  }, [currentInstructor?.id, loadData]);

  const toggleExpand = async (courseId) => {
    if (expandedCourse === courseId) {
      setExpandedCourse(null);
      return;
    }
    setExpandedCourse(courseId);
    if (!lessonsMap[courseId]) {
      try {
        const res = await getLessons({ course_id: courseId });
        setLessonsMap(prev => ({ ...prev, [courseId]: res.data }));
      } catch (err) { console.error(err); }
    }
  };

  if (loading) {
    return <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>My Courses</h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">{courses.length} courses assigned to you</p>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <p className="text-[#062B1A]/50">No courses assigned</p>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map(course => (
            <Card key={course.id} className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleExpand(course.id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#062B1A] flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-[#062B1A]">{course.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-[#062B1A]/40 mt-0.5">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{course.enrollment_count} students</span>
                        <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{course.lessons_count} lessons</span>
                        <Badge variant="outline" className="text-[10px]">{course.type}</Badge>
                        <Badge className={`text-[10px] border-0 ${course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{course.status}</Badge>
                      </div>
                    </div>
                  </div>
                  {expandedCourse === course.id ? <ChevronUp className="w-4 h-4 text-[#062B1A]/40" /> : <ChevronDown className="w-4 h-4 text-[#062B1A]/40" />}
                </div>

                {expandedCourse === course.id && (
                  <div className="mt-4 pt-4 border-t border-[rgba(6, 43, 26,0.08)]">
                    <div className="mb-3 flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                        onClick={() => navigate(`/instructor/courses/${course.id}`)}
                      >
                        Open course workspace
                      </Button>
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40 mb-2">Lessons</p>
                    {lessonsMap[course.id]?.length === 0 ? (
                      <p className="text-sm text-[#062B1A]/40">No lessons</p>
                    ) : (
                      <div className="space-y-1">
                        {lessonsMap[course.id]?.map((lesson, idx) => (
                          <div key={lesson.id} className="flex items-center gap-3 p-2 rounded bg-[#F7F8FA]">
                            <span className="text-xs font-medium text-[#062B1A]/40 w-6">{idx + 1}.</span>
                            <span className="text-sm text-[#062B1A]">{lesson.title}</span>
                            <Badge variant="outline" className="text-[10px] ml-auto">{lesson.type}</Badge>
                            <span className="text-xs text-[#062B1A]/40">{lesson.duration_minutes}m</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
